import { createContext, useContext, useState, useEffect, useRef } from 'react';
import API from '../api';
// 2026-09 요청: "전자메일 캐시는 서버 메모리가 아니라 접속자 개인 PC의 메모리에 넣어야 한다
// (동시접속자가 많을 경우 서버과부하 방지)" — 전자메일 캐시(frontend/src/utils/
// emailClientCache.js)는 이 브라우저 탭의 메모리에만 존재한다. clearSessionLocally()는
// 수동 로그아웃/자동(비활동) 로그아웃/다른 탭에서의 로그아웃 방송 수신을 포함해 "로그아웃이
// 이 탭에 로컬로 반영되는 모든 경로"가 결국 모이는 단일 지점이라, 여기서 emailCache.clearAll()을
// 호출하는 것만으로 모든 로그아웃 경로에 대해 "로그아웃되면 이메일 캐시 삭제" 요구사항을
// 만족시킬 수 있다.
import * as emailCache from '../utils/emailClientCache';
import * as mailSync from '../utils/mailSync';

const AuthContext = createContext();
const CHANNEL_NAME = 'ims_auth';
const ACTIVITY_THROTTLE_MS = 30 * 1000; // 활동 기록(및 다른 탭으로의 방송)은 최대 30초에 1번만

export function AuthProvider({ children }) {
  // 로그인 정보를 localStorage가 아니라 sessionStorage에 저장한다. sessionStorage는 새로고침이나
  // 같은 탭 안에서의 페이지 이동에는 그대로 유지되지만(그래서 "새로고침해도 로그인 유지"가
  // 여전히 보장됨), 브라우저(모든 탭)를 완전히 종료하면 자동으로 지워진다 — "인터넷 브라우저를
  // 완전히 종료하면 로그아웃되어야 한다"는 요구사항을 별도 이벤트 처리 없이 브라우저 자체
  // 동작만으로 만족시키기 위함이다.
  //
  // 다만 sessionStorage는 탭마다 독립적이라, 그대로 두면 "이미 로그인된 브라우저에서 새 탭을
  // 하나 더 열면 그 탭은 다시 로그인해야 함"이라는 불편함이 생긴다. 이를 BroadcastChannel로
  // 보완한다: 세션이 없는 새 탭이 뜨면 같은 브라우저의 다른 탭에게 "혹시 로그인된 세션 있으면
  // 나눠달라"고 요청하고, 있으면 그 탭이 응답해서 세션을 그대로 넘겨받는다. 반대로 어느 탭에서
  // 로그아웃(수동/자동/서버재시작 등 무엇이든)이 일어나면 다른 모든 탭에도 즉시 방송되어 함께
  // 로그아웃된다 — 한쪽에서 보안상 로그아웃했는데 다른 탭은 계속 로그인 상태로 남아있는 상황을
  // 막기 위함이다. 이 방식은 "완전히 새로운" 탭을 여는 경우에만 아주 짧은(최대 0.4초) 확인
  // 시간이 필요하고, 열려있는 다른 탭이 하나도 없거나 전부 로그아웃 상태라면 결국 로그인 화면이
  // 뜬다 — 즉 "브라우저를 완전히 종료하면 로그아웃"이라는 원래 요구사항은 그대로 유지된다.
  const channelRef = useRef(null);
  const lastActivityWriteRef = useRef(0);

  const [authLoading, setAuthLoading] = useState(() => !sessionStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    try {
      const saved = sessionStorage.getItem('user');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  // 세션 값을 로컬(sessionStorage + React state)에 반영만 하는 도우미 — 서버 호출이나 다른
  // 탭으로의 방송은 하지 않는다(호출하는 쪽에서 상황에 맞게 결정).
  const applySessionLocally = (userData, token, loginLogId) => {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    if (loginLogId != null) sessionStorage.setItem('loginLogId', String(loginLogId));
    if (!sessionStorage.getItem('lastActivityAt')) sessionStorage.setItem('lastActivityAt', String(Date.now()));
    setUser(userData);
  };

  const clearSessionLocally = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('loginLogId');
    sessionStorage.removeItem('lastActivityAt');
    setUser(null);
    // 이 브라우저 탭에 쌓인 전자메일 캐시(폴더/메일 목록)도 함께 비운다 — 로그아웃 경로가
    // 무엇이든(수동/자동/다른 탭 방송) 이 함수 하나만 거치면 되므로 여기서 처리한다.
    emailCache.clearAll();
    mailSync.stop(); // PC에 내려받은 메일(RAM+디스크)과 5분 확인 타이머 삭제
  };

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      // 아주 오래된 브라우저 등 미지원 환경 — 다중 탭 동기화 없이 기존처럼 동작
      setAuthLoading(false);
      return;
    }
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (ev) => {
      const msg = ev.data || {};
      switch (msg.type) {
        case 'REQUEST_SESSION': {
          // 새로 뜬 다른 탭이 세션을 나눠달라고 요청 — 나(이 탭)에게 유효한 세션이 있으면 응답
          const token = sessionStorage.getItem('token');
          const savedUser = sessionStorage.getItem('user');
          if (token && savedUser) {
            try {
              channel.postMessage({ type: 'SESSION_DATA', token, user: JSON.parse(savedUser), loginLogId: sessionStorage.getItem('loginLogId') });
            } catch (e) { /* ignore */ }
          }
          break;
        }
        case 'SESSION_DATA':
          // 내가 세션 요청을 보낸 새 탭이었던 경우에만 반영 (이미 세션이 있으면 무시)
          if (!sessionStorage.getItem('token')) {
            applySessionLocally(msg.user, msg.token, msg.loginLogId);
          }
          setAuthLoading(false);
          break;
        case 'LOGGED_IN':
          applySessionLocally(msg.user, msg.token, msg.loginLogId);
          break;
        case 'LOGGED_OUT':
          clearSessionLocally();
          break;
        case 'ACTIVITY': {
          // 다른 탭에서의 활동(또는 생산전광판 화면 예외 처리)을 이 탭의 유휴시간 계산에도 반영
          const current = Number(sessionStorage.getItem('lastActivityAt') || 0);
          if (msg.at > current) sessionStorage.setItem('lastActivityAt', String(msg.at));
          break;
        }
        default:
          break;
      }
    };

    if (!sessionStorage.getItem('token')) {
      // 2026-09(미리보기 환경 전용) — IMS_미리보기_시작.bat이 REACT_APP_STAGING_MODE=true로
      // 빌드한 경우에만, 로그인 화면 없이 서버의 dev-auto-login으로 즉시 로그인한다. 운영
      // 빌드(IMS_빌드후시작.bat)에는 이 환경변수가 없으므로 이 분기는 절대 실행되지 않는다.
      if (process.env.REACT_APP_STAGING_MODE === 'true') {
        API.get('/auth/dev-auto-login')
          .then(res => applySessionLocally(res.data.user, res.data.token, res.data.loginLogId))
          .catch(() => { /* 실패 시 일반 로그인 화면으로 진행 */ })
          .finally(() => setAuthLoading(false));
        return () => channel.close();
      }
      // 이 탭엔 세션이 없음 — 다른 탭에 있는지 짧게 확인해보고, 없으면 로그인 화면으로
      channel.postMessage({ type: 'REQUEST_SESSION' });
      const t = setTimeout(() => setAuthLoading(false), 400);
      return () => { clearTimeout(t); channel.close(); };
    }

    setAuthLoading(false);
    return () => channel.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 로그인 처리 (loginLogId: 로그인 기록에서 이 세션에 해당하는 행 id — 로그아웃 시 종료시간 기록에 사용)
  const login = (userData, token, loginLogId) => {
    applySessionLocally(userData, token, loginLogId);
    channelRef.current?.postMessage({ type: 'LOGGED_IN', token, user: userData, loginLogId });
  };

  // 로그아웃 처리 — 로그인 기록의 종료시간(logout_time)을 남기기 위해 토큰을 지우기 전에
  // 서버 로그아웃 API를 먼저 호출한다. 서버 호출이 실패(오프라인, 서버 재시작으로 토큰이 이미
  // 무효인 경우 등)해도 로컬 로그아웃은 그대로 진행한다.
  // reason: 'manual'(사용자가 직접 로그아웃 버튼 클릭) | 'inactivity'(3시간 미사용 자동
  // 로그아웃) — login_logs.logout_reason에 그대로 기록되어 나중에 왜 로그아웃됐는지 추적할 수 있다.
  const logout = async (reason = 'manual') => {
    const loginLogId = sessionStorage.getItem('loginLogId');
    try {
      if (loginLogId) await API.post('/auth/logout', { loginLogId: Number(loginLogId), reason });
    } catch (e) {
      // 서버 호출 실패해도 로컬 로그아웃은 계속 진행
    }
    clearSessionLocally();
    // 다른 탭들도 함께 로그아웃되도록 방송 — 한 탭에서만 로그아웃되고 나머지 탭은 계속 로그인
    // 상태로 남아있는 상황(보안상 바람직하지 않음)을 막는다.
    channelRef.current?.postMessage({ type: 'LOGGED_OUT' });
  };

  // 사용자가 실제로 화면을 조작했다는 신호 — 3시간 미사용 자동 로그아웃(components/AutoLogout.js)
  // 이 호출한다. 너무 자주 쓰지 않도록 30초에 한 번만 실제로 기록/방송하며(force=true면 무시하고
  // 즉시), 다른 탭에도 방송해서 "여러 탭 중 어느 하나라도 활동 중이면 전체가 유휴 상태로 보지
  // 않도록" 한다(생산전광판 화면 예외 처리도 이 방송을 통해 다른 탭에 전파된다).
  const touchActivity = (force = false) => {
    const now = Date.now();
    if (!force && now - lastActivityWriteRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityWriteRef.current = now;
    sessionStorage.setItem('lastActivityAt', String(now));
    channelRef.current?.postMessage({ type: 'ACTIVITY', at: now });
  };

  // 로그인(또는 새로고침 후 세션 복원)과 동시에 전자메일을 PC로 내려받고 5분 주기 확인 시작.
  // 생산현황판 전용 계정은 메일을 쓰지 않으므로 제외.
  useEffect(() => {
    if (user?.username && user.accountType !== 'display_only') mailSync.start(user.username);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  return (
    <AuthContext.Provider value={{ user, login, logout, authLoading, touchActivity }}>
      {children}
    </AuthContext.Provider>
  );
}

// 어디서든 user 정보 꺼내 쓸 수 있는 훅
export function useAuth() {
  return useContext(AuthContext);
}
